// @ts-nocheck
/**
 * CardListScreen - カード一覧画面
 *
 * フィルター、検索、グリッド表示を提供する
 */

import React, { useState, useMemo } from 'react';
import { useCardDex } from '../../hooks/useCardDex';
import { useIsMobile } from '../../../hooks/useMediaQuery';
import {
  filterStyles,
  gridStyles,
  COLORS,
} from '../../styles/cardDexStyles';
import { ATTRIBUTE_COLORS } from '../../../../utils/constants';
import { useCardData } from '../CardDexScreen';
import { RangeSlider } from '../common';
import { KEYWORD_ABILITIES } from '../../../../engine/keywordAbilities';
import { CATEGORIES } from '../../../market/data/categories';
import {
  addToWishlist,
  removeFromWishlist,
  isInWishlist,
} from '../../../merchant';
import { CardV2 } from '../../../../components/cardV2';
import { CARD_DISPLAY_SIZES } from '../../../../components/cardV2/cardConstants';

// ========================================
// キーワード定義（KEYWORD_ABILITIESから取得）
// ========================================

const KEYWORDS = Object.values(KEYWORD_ABILITIES);

// ========================================
// カテゴリ定義（CATEGORIESから取得）
// ========================================

const CARD_CATEGORIES = CATEGORIES;

// ========================================
// フィルターパネル
// ========================================

const FilterPanel = () => {
  const {
    filters,
    toggleFilterValue,
    updateFilter,
    searchQuery,
    setSearchQuery,
    resetFilters,
    settings,
    toggleShowWishlistHearts,
  } = useCardDex();
  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(!isMobile);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const attributes = ['炎', '水', '光', '闇', '原始', '未来', 'なし'];
  // CSVのtype値は英語なので、英語→日本語のマッピングを使用
  const cardTypes = [
    { key: 'monster', label: 'モンスター' },
    { key: 'magic', label: '魔法' },
    { key: 'field', label: 'フィールド' },
    { key: 'phasecard', label: 'フェイズ' },
  ];
  const ownedOptions = [
    { value: 'all', label: '全て' },
    { value: 'owned', label: '所持' },
    { value: 'not-owned', label: '未所持' },
  ];

  // 詳細フィルターがアクティブかどうか
  const hasAdvancedFilters = filters.costValues.length > 0 ||
    filters.keyword.length > 0 ||
    filters.category.length > 0 ||
    filters.attack[0] > 0 || filters.attack[1] < 10000 ||
    filters.hp[0] > 0 || filters.hp[1] < 10000 ||
    filters.effectSearch || filters.forbidden;

  return (
    <div style={filterStyles.panel}>
      {/* 検索バー */}
      <div style={filterStyles.row}>
        <input
          type="text"
          placeholder="カード名で検索..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={filterStyles.searchInput}
        />
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            ...filterStyles.filterButton,
            minWidth: '80px',
          }}
        >
          {isExpanded ? '▲ 閉じる' : '▼ フィルター'}
        </button>
        <button
          onClick={resetFilters}
          style={{
            ...filterStyles.filterButton,
            color: COLORS.textDanger,
          }}
        >
          リセット
        </button>
      </div>

      {isExpanded && (
        <>
          {/* 属性フィルター */}
          <div style={filterStyles.row}>
            <span style={filterStyles.label}>属性:</span>
            {attributes.map((attr) => (
              <button
                key={attr}
                onClick={() => toggleFilterValue('attribute', attr)}
                style={{
                  ...filterStyles.filterButton,
                  ...(filters.attribute.includes(attr)
                    ? filterStyles.filterButtonActive
                    : {}),
                  borderColor: ATTRIBUTE_COLORS[attr]?.primary || COLORS.borderSecondary,
                }}
              >
                {attr}
              </button>
            ))}
          </div>

          {/* 種類フィルター */}
          <div style={filterStyles.row}>
            <span style={filterStyles.label}>種類:</span>
            {cardTypes.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => toggleFilterValue('cardType', key)}
                style={{
                  ...filterStyles.filterButton,
                  ...(filters.cardType.includes(key)
                    ? filterStyles.filterButtonActive
                    : {}),
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* 所持状態フィルター */}
          <div style={filterStyles.row}>
            <span style={filterStyles.label}>所持:</span>
            {ownedOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => updateFilter('owned', opt.value)}
                style={{
                  ...filterStyles.filterButton,
                  ...(filters.owned === opt.value
                    ? filterStyles.filterButtonActive
                    : {}),
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* ウィッシュリストハート表示 */}
          <div style={{ ...filterStyles.row, marginTop: '8px' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                color: COLORS.textPrimary,
                fontSize: '13px',
              }}
            >
              <input
                type="checkbox"
                checked={settings.showWishlistHearts}
                onChange={toggleShowWishlistHearts}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                欲しいものハートを表示
                <span style={{ fontSize: '16px' }}>❤️</span>
              </span>
            </label>
          </div>

          {/* 詳細フィルター展開ボタン */}
          <div style={{ marginTop: '8px', borderTop: `1px solid ${COLORS.borderSecondary}`, paddingTop: '8px' }}>
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              style={{
                ...filterStyles.filterButton,
                width: '100%',
                background: hasAdvancedFilters ? 'rgba(107, 76, 230, 0.3)' : 'rgba(30, 30, 50, 0.6)',
              }}
            >
              {showAdvanced ? '▲ 詳細フィルターを閉じる' : '▼ 詳細フィルター'}
              {hasAdvancedFilters && ' (有効)'}
            </button>
          </div>

          {/* 詳細フィルター */}
          {showAdvanced && (
            <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(20, 20, 40, 0.4)', borderRadius: '8px' }}>
              {/* コストフィルター */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={filterStyles.label}>コスト:</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((cost) => (
                    <button
                      key={cost}
                      onClick={() => toggleFilterValue('costValues', cost)}
                      style={{
                        ...filterStyles.filterButton,
                        fontSize: '11px',
                        padding: '4px 10px',
                        minWidth: '32px',
                        ...(filters.costValues.includes(cost)
                          ? filterStyles.filterButtonActive
                          : {}),
                      }}
                    >
                      {cost}
                    </button>
                  ))}
                </div>
              </div>

              {/* キーワードフィルター */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={filterStyles.label}>キーワード:</span>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      onClick={() => updateFilter('keywordMode', 'or')}
                      style={{
                        ...filterStyles.filterButton,
                        fontSize: '10px',
                        padding: '2px 6px',
                        ...(filters.keywordMode === 'or' ? filterStyles.filterButtonActive : {}),
                      }}
                    >
                      OR
                    </button>
                    <button
                      onClick={() => updateFilter('keywordMode', 'and')}
                      style={{
                        ...filterStyles.filterButton,
                        fontSize: '10px',
                        padding: '2px 6px',
                        ...(filters.keywordMode === 'and' ? filterStyles.filterButtonActive : {}),
                      }}
                    >
                      AND
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
                  {KEYWORDS.map((kw) => (
                    <button
                      key={kw}
                      onClick={() => toggleFilterValue('keyword', kw)}
                      style={{
                        ...filterStyles.filterButton,
                        fontSize: '11px',
                        padding: '4px 8px',
                        ...(filters.keyword.includes(kw)
                          ? filterStyles.filterButtonActive
                          : {}),
                      }}
                    >
                      {kw}
                    </button>
                  ))}
                </div>
              </div>

              {/* カテゴリフィルター */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={filterStyles.label}>カテゴリ:</span>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      onClick={() => updateFilter('categoryMode', 'or')}
                      style={{
                        ...filterStyles.filterButton,
                        fontSize: '10px',
                        padding: '2px 6px',
                        ...(filters.categoryMode === 'or' ? filterStyles.filterButtonActive : {}),
                      }}
                    >
                      OR
                    </button>
                    <button
                      onClick={() => updateFilter('categoryMode', 'and')}
                      style={{
                        ...filterStyles.filterButton,
                        fontSize: '10px',
                        padding: '2px 6px',
                        ...(filters.categoryMode === 'and' ? filterStyles.filterButtonActive : {}),
                      }}
                    >
                      AND
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
                  {CARD_CATEGORIES.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => toggleFilterValue('category', cat)}
                      style={{
                        ...filterStyles.filterButton,
                        fontSize: '11px',
                        padding: '4px 8px',
                        ...(filters.category.includes(cat)
                          ? filterStyles.filterButtonActive
                          : {}),
                      }}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* 攻撃力範囲 */}
              <RangeSlider
                label="攻撃力"
                min={0}
                max={10000}
                step={500}
                value={filters.attack}
                onChange={(value) => updateFilter('attack', value)}
              />

              {/* HP範囲 */}
              <RangeSlider
                label="HP"
                min={0}
                max={10000}
                step={500}
                value={filters.hp}
                onChange={(value) => updateFilter('hp', value)}
              />

              {/* 効果テキスト検索 */}
              <div style={{ marginBottom: '12px' }}>
                <div style={filterStyles.label}>効果検索:</div>
                <input
                  type="text"
                  placeholder="効果テキストを検索..."
                  value={filters.effectSearch}
                  onChange={(e) => updateFilter('effectSearch', e.target.value)}
                  style={{
                    ...filterStyles.searchInput,
                    width: '100%',
                    marginTop: '8px',
                  }}
                />
              </div>

              {/* 禁忌フィルター */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="forbidden-filter"
                  checked={filters.forbidden}
                  onChange={(e) => updateFilter('forbidden', e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <label htmlFor="forbidden-filter" style={{ color: COLORS.textPrimary, cursor: 'pointer' }}>
                  禁忌カードのみ表示
                </label>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ========================================
// カードグリッド
// ========================================

const CardGrid = ({
  cards,
  ownedMap,
  onCardClick,
  showWishlistHearts,
  wishlistSet,
  onToggleWishlist,
}) => {
  const isMobile = useIsMobile();
  const displayWidth = isMobile ? CARD_DISPLAY_SIZES.dexListMobile.width : CARD_DISPLAY_SIZES.dexList.width;

  return (
    <div
      style={{
        ...gridStyles.container,
        ...(isMobile ? gridStyles.containerMobile : {}),
      }}
    >
      {cards.map((card) => {
        const ownedInfo = ownedMap[card.id] || { owned: false, rarities: [] };
        return (
          <CardV2
            key={card.id}
            card={card}
            displayMode="dex-list"
            displayWidth={displayWidth}
            isOwned={ownedInfo.owned}
            ownedBadgeText={ownedInfo.owned ? `${ownedInfo.rarities.length}種` : undefined}
            showNotOwnedMark={!ownedInfo.owned}
            showWishlistHeart={showWishlistHearts}
            inWishlist={wishlistSet?.has(card.id)}
            onWishlistToggle={(e) => {
              e.stopPropagation();
              onToggleWishlist?.(card.id);
            }}
            showBottomName={true}
            showBottomCost={true}
            onClick={() => onCardClick(card.id)}
            isMobile={isMobile}
          />
        );
      })}
    </div>
  );
};

// ========================================
// ソートセレクター
// ========================================

const SortSelector = () => {
  const { sortBy, sortOrder, updateSort } = useCardDex();

  const sortOptions = [
    { value: 'id', label: 'ID順' },
    { value: 'name', label: '名前順' },
    { value: 'cost', label: 'コスト順' },
    { value: 'attack', label: '攻撃力順' },
    { value: 'hp', label: 'HP順' },
  ];

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
      <span style={{ fontSize: '13px', color: COLORS.textSecondary }}>並び替え:</span>
      <select
        value={sortBy}
        onChange={(e) => updateSort(e.target.value)}
        style={{
          padding: '6px 12px',
          borderRadius: '6px',
          background: 'rgba(30, 30, 50, 0.8)',
          color: COLORS.textPrimary,
          border: `1px solid ${COLORS.borderSecondary}`,
          outline: 'none',
        }}
      >
        {sortOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <button
        onClick={() => updateSort(sortBy)}
        style={{
          ...filterStyles.filterButton,
          padding: '6px 10px',
        }}
      >
        {sortOrder === 'asc' ? '▲' : '▼'}
      </button>
    </div>
  );
};

// ========================================
// メイン画面
// ========================================

const CardListScreen = () => {
  const { filters, searchQuery, sortBy, sortOrder, navigateToDetail, settings } = useCardDex();
  const { allCards, collection, playerData, updatePlayerData } = useCardData();

  // プレイヤーの所持カード情報
  const ownedMap = useMemo(() => {
    const map = {};

    collection.forEach((entry) => {
      if (!map[entry.cardId]) {
        map[entry.cardId] = { owned: true, rarities: [] };
      }
      if (!map[entry.cardId].rarities.includes(entry.rarity)) {
        map[entry.cardId].rarities.push(entry.rarity);
      }
    });

    return map;
  }, [collection]);

  // ウィッシュリストSet
  const merchantData = playerData?.merchantData || {};
  const wishlistSet = useMemo(() => {
    const wishlist = merchantData.wishlist || [];
    return new Set(wishlist);
  }, [merchantData.wishlist]);

  // ウィッシュリストトグル
  const handleToggleWishlist = (cardId) => {
    if (!updatePlayerData || !playerData) return;

    const inWishlist = isInWishlist(merchantData, cardId);
    const newMerchantData = inWishlist
      ? removeFromWishlist(merchantData, cardId)
      : addToWishlist(merchantData, cardId);

    if (newMerchantData !== merchantData) {
      updatePlayerData({
        ...playerData,
        merchantData: newMerchantData,
      });
    }
  };

  // フィルター適用
  const filteredCards = useMemo(() => {
    let result = [...allCards];

    // 名前検索
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((card) =>
        card.name.toLowerCase().includes(query) ||
        card.effect?.toLowerCase().includes(query)
      );
    }

    // 属性フィルター
    if (filters.attribute.length > 0) {
      result = result.filter((card) => filters.attribute.includes(card.attribute));
    }

    // 種類フィルター
    if (filters.cardType.length > 0) {
      result = result.filter((card) => filters.cardType.includes(card.type));
    }

    // コストフィルター（個別選択が優先、なければ範囲フィルター）
    if (filters.costValues && filters.costValues.length > 0) {
      // 個別コスト選択
      result = result.filter((card) => filters.costValues.includes(card.cost));
    } else {
      // 範囲フィルター
      const [minCost, maxCost] = filters.cost;
      result = result.filter((card) => card.cost >= minCost && card.cost <= maxCost);
    }

    // 所持状態フィルター
    if (filters.owned === 'owned') {
      result = result.filter((card) => ownedMap[card.id]?.owned);
    } else if (filters.owned === 'not-owned') {
      result = result.filter((card) => !ownedMap[card.id]?.owned);
    }

    // ===== 詳細フィルター（Phase 1.5追加）=====

    // キーワードフィルター
    if (filters.keyword && filters.keyword.length > 0) {
      result = result.filter((card) => {
        if (!card.keyword) return false;
        if (filters.keywordMode === 'and') {
          // AND: 選択したキーワードを全て持つ
          return filters.keyword.every((kw) => card.keyword.includes(kw));
        } else {
          // OR: 選択したキーワードのいずれかを持つ
          return filters.keyword.some((kw) => card.keyword.includes(kw));
        }
      });
    }

    // カテゴリフィルター
    if (filters.category && filters.category.length > 0) {
      result = result.filter((card) => {
        if (!card.category) return false;
        // card.categoryは配列形式（例: ['ドラゴン', 'スライム']）
        const cardCategories = Array.isArray(card.category) ? card.category : [];
        if (filters.categoryMode === 'and') {
          // AND: 選択したカテゴリを全て持つ
          return filters.category.every((cat) => cardCategories.includes(cat));
        } else {
          // OR: 選択したカテゴリのいずれかを持つ
          return filters.category.some((cat) => cardCategories.includes(cat));
        }
      });
    }

    // 攻撃力フィルター
    if (filters.attack && (filters.attack[0] > 0 || filters.attack[1] < 10000)) {
      result = result.filter((card) => {
        if (card.type !== 'monster') return true; // モンスター以外はパス
        const attack = parseInt(card.attack) || 0;
        return attack >= filters.attack[0] && attack <= filters.attack[1];
      });
    }

    // HPフィルター
    if (filters.hp && (filters.hp[0] > 0 || filters.hp[1] < 10000)) {
      result = result.filter((card) => {
        if (card.type !== 'monster') return true; // モンスター以外はパス
        const hp = parseInt(card.hp) || 0;
        return hp >= filters.hp[0] && hp <= filters.hp[1];
      });
    }

    // 効果テキスト検索
    if (filters.effectSearch) {
      const searchTerm = filters.effectSearch.toLowerCase();
      result = result.filter((card) =>
        card.effect?.toLowerCase().includes(searchTerm)
      );
    }

    // 禁忌フィルター
    if (filters.forbidden) {
      result = result.filter((card) => card.isForbidden === true || (Array.isArray(card.keyword) && card.keyword.includes('禁忌カード')));
    }

    return result;
  }, [allCards, filters, searchQuery, ownedMap]);

  // ソート適用
  const sortedCards = useMemo(() => {
    const result = [...filteredCards];

    result.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'id':
          comparison = a.id.localeCompare(b.id);
          break;
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'cost':
          comparison = (a.cost || 0) - (b.cost || 0);
          break;
        case 'attack':
          comparison = (a.attack || 0) - (b.attack || 0);
          break;
        case 'hp':
          comparison = (a.hp || 0) - (b.hp || 0);
          break;
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [filteredCards, sortBy, sortOrder]);

  // 統計
  const totalCount = allCards.length;
  const ownedCount = Object.keys(ownedMap).length;
  const displayCount = sortedCards.length;

  return (
    <div>
      {/* 統計表示 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
          padding: '12px 16px',
          background: 'rgba(20, 20, 40, 0.6)',
          borderRadius: '8px',
        }}
      >
        <span style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
          所持: <span style={{ color: COLORS.textGold, fontWeight: 'bold' }}>{ownedCount}</span> / {totalCount} 種
          <span style={{ marginLeft: '8px', color: COLORS.textSuccess }}>
            ({Math.round((ownedCount / totalCount) * 100)}%)
          </span>
        </span>
        <span style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
          表示: {displayCount} 件
        </span>
      </div>

      {/* フィルターパネル */}
      <FilterPanel />

      {/* ソートセレクター */}
      <SortSelector />

      {/* カードグリッド */}
      <CardGrid
        cards={sortedCards}
        ownedMap={ownedMap}
        onCardClick={(cardId) => navigateToDetail(cardId)}
        showWishlistHearts={settings.showWishlistHearts}
        wishlistSet={wishlistSet}
        onToggleWishlist={handleToggleWishlist}
      />

      {/* 結果なし */}
      {sortedCards.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: '40px',
            color: COLORS.textSecondary,
          }}
        >
          条件に一致するカードがありません
        </div>
      )}
    </div>
  );
};

export default CardListScreen;
